import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    AlertTriangle,
    ArrowRight,
    CalendarClock,
    CircleDollarSign,
    Cpu,
    Loader2,
    Monitor,
    Package,
    Plus,
    RefreshCw,
    TrendingUp,
    Truck,
    Users,
    Wallet,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../hooks/useApi";
import { Customer, CustomerProduct } from "../types";
import { daysUntil, formatCompact, formatDate, formatMoney } from "../utils/format";
import Card, { CardHeader } from "../components/ui/Card";
import Button from "../components/ui/Button";
import { StatusBadge } from "../components/ui/Badge";
import { EmptyState } from "../components/ui/DataTable";
import { RenewalChip } from "./customerProducts/CustomerProductTable";
import CustomerFormModal from "./customers/CustomerFormModal";
import CustomerProductFormModal from "./customerProducts/CustomerProductFormModal";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Dashboard() {
    const { user, hasRole } = useAuth();
    const isAdmin = hasRole("SUPERADMIN");
    const navigate = useNavigate();

    const customersReq = useApi<Customer[]>("/customers");
    const productsReq = useApi<CustomerProduct[]>("/customer-product");

    const [customerFormOpen, setCustomerFormOpen] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);

    const loading = (customersReq.loading && !customersReq.data) || (productsReq.loading && !productsReq.data);
    const error = customersReq.error || productsReq.error;

    const reloadAll = () => {
        customersReq.reload();
        productsReq.reload();
    };

    const stats = useMemo(() => {
        const customers = customersReq.data ?? [];
        const rows = productsReq.data ?? [];
        const software = rows.filter((r) => r.product.type === "SOFTWARE");
        const hardware = rows.filter((r) => r.product.type === "HARDWARE");

        const liveSoftware = software.filter((r) => r.status !== "CANCELLED" && r.renewalDate);
        const withDays = liveSoftware.map((r) => ({ r, d: daysUntil(r.renewalDate)! }));
        const overdue = withDays.filter((x) => x.d < 0);
        const dueSoon = withDays.filter((x) => x.d >= 0 && x.d <= 30);

        const unpaid = rows.filter((r) => r.paymentStatus !== "PAID");
        const pendingDeliveries = hardware
            .filter((r) => r.deliveryStatus === "PENDING" || r.deliveryStatus === "IN_PROGRESS")
            .sort((a, b) => (a.deliveryDate ?? "9999").localeCompare(b.deliveryDate ?? "9999"));

        // Sales per month for the last 6 months (by created date)
        const now = new Date();
        const months = Array.from({ length: 6 }, (_, i) => {
            const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
            return { key: `${d.getFullYear()}-${d.getMonth()}`, label: MONTHS[d.getMonth()]!, hardware: 0, software: 0 };
        });
        for (const r of rows) {
            const d = new Date(r.createdAt);
            const m = months.find((x) => x.key === `${d.getFullYear()}-${d.getMonth()}`);
            if (m) m[r.product.type === "HARDWARE" ? "hardware" : "software"] += Number(r.amount);
        }

        const count = <T extends string>(list: CustomerProduct[], pick: (r: CustomerProduct) => T | null) =>
            list.reduce<Record<string, number>>((acc, r) => {
                const k = pick(r);
                if (k) acc[k] = (acc[k] ?? 0) + 1;
                return acc;
            }, {});

        return {
            customers: customers.length,
            totalValue: rows.reduce((s, r) => s + Number(r.amount), 0),
            hardwareCount: hardware.length,
            softwareCount: software.length,
            activeSubs: software.filter((r) => r.status === "ACTIVE").length,
            overdue: overdue.sort((a, b) => a.d - b.d).map((x) => x.r),
            dueSoon: dueSoon.sort((a, b) => a.d - b.d).map((x) => x.r),
            expectedRenewals: [...overdue, ...dueSoon].reduce((s, x) => s + Number(x.r.renewalAmount ?? x.r.amount), 0),
            unpaidCount: unpaid.length,
            unpaidValue: unpaid.reduce((s, r) => s + Number(r.amount), 0),
            pendingDeliveries,
            months,
            payment: count(rows, (r) => r.paymentStatus),
            subStatus: count(software, (r) => r.status),
            recentCustomers: [...customers].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 5),
        };
    }, [customersReq.data, productsReq.data]);

    const greeting = (() => {
        const h = new Date().getHours();
        return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
    })();

    if (loading) {
        return (
            <div className="flex justify-center py-32">
                <Loader2 className="text-[#8b5cf6] animate-spin" size={40} />
            </div>
        );
    }

    return (
        <>
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                <div>
                    <p className="text-sm text-slate-500">
                        {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                    </p>
                    <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight mt-1">
                        {greeting}, {user?.name?.split(" ")[0]}
                    </h1>
                    <p className="text-slate-400 text-sm mt-1">
                        {stats.overdue.length > 0
                            ? `${stats.overdue.length} subscription${stats.overdue.length > 1 ? "s are" : " is"} past renewal. Worth a follow-up today.`
                            : stats.dueSoon.length > 0
                              ? `${stats.dueSoon.length} renewal${stats.dueSoon.length > 1 ? "s" : ""} coming up in the next 30 days.`
                              : "Everything is on track."}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="ghost" icon={RefreshCw} onClick={reloadAll} loading={customersReq.loading || productsReq.loading}>
                        Refresh
                    </Button>
                    <Button variant="secondary" icon={Users} onClick={() => setCustomerFormOpen(true)}>
                        Add customer
                    </Button>
                    <Button icon={Plus} onClick={() => setAssignOpen(true)}>
                        Assign product
                    </Button>
                </div>
            </motion.div>

            {error && (
                <div className="mb-6 flex items-center gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    <AlertTriangle size={18} />
                    {error}
                </div>
            )}

            {/* Stat cards */}
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                <StatCard
                    index={0}
                    to="/customers"
                    icon={Users}
                    label="Customers"
                    value={String(stats.customers)}
                    sub={`${stats.hardwareCount + stats.softwareCount} products assigned`}
                />
                <StatCard
                    index={1}
                    to="/customer-products?type=SOFTWARE&status=ACTIVE"
                    icon={CalendarClock}
                    label="Active subscriptions"
                    value={String(stats.activeSubs)}
                    sub={`${stats.softwareCount} software total`}
                />
                <StatCard
                    index={2}
                    to="/renewals"
                    icon={AlertTriangle}
                    label="Renewals (30d)"
                    value={String(stats.dueSoon.length + stats.overdue.length)}
                    sub={stats.overdue.length ? `${stats.overdue.length} overdue` : `${formatMoney(stats.expectedRenewals)} expected`}
                    alert={stats.overdue.length > 0}
                />
                <StatCard
                    index={3}
                    to="/customer-products?paymentStatus=NOT_PAID"
                    icon={Wallet}
                    label="Unpaid / partial"
                    value={String(stats.unpaidCount)}
                    sub={`${formatMoney(stats.unpaidValue)} billed`}
                    alert={stats.unpaidCount > 0}
                />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Sales chart */}
                <Card className="xl:col-span-2">
                    <CardHeader
                        title="Sales"
                        subtitle="Amount of products assigned per month, last 6 months"
                        icon={TrendingUp}
                        action={
                            <div className="text-right">
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">All time</p>
                                <p className="text-lg font-bold text-white">{formatMoney(stats.totalValue)}</p>
                            </div>
                        }
                    />
                    <SalesChart months={stats.months} />
                </Card>

                {/* Payment donut */}
                <Card>
                    <CardHeader title="Payments" subtitle="All customer products" icon={CircleDollarSign} />
                    <Donut
                        segments={[
                            { label: "Paid", value: stats.payment.PAID ?? 0, color: "#34d399", to: "/customer-products?paymentStatus=PAID" },
                            { label: "Partially paid", value: stats.payment.PARTIALLY_PAID ?? 0, color: "#fbbf24", to: "/customer-products?paymentStatus=PARTIALLY_PAID" },
                            { label: "Not paid", value: stats.payment.NOT_PAID ?? 0, color: "#f87171", to: "/customer-products?paymentStatus=NOT_PAID" },
                        ]}
                    />
                </Card>

                {/* Upcoming renewals */}
                <Card className="xl:col-span-2" padded={false}>
                    <div className="p-5 md:p-6 pb-0 md:pb-0">
                        <CardHeader
                            title="Renewals"
                            subtitle="Overdue and due in the next 30 days"
                            icon={CalendarClock}
                            action={<ViewAll to="/renewals" />}
                        />
                    </div>
                    {stats.overdue.length + stats.dueSoon.length === 0 ? (
                        <EmptyState title="No renewals due" text="Nothing is overdue or due in the next 30 days." />
                    ) : (
                        <ul className="divide-y divide-slate-800/60">
                            {[...stats.overdue, ...stats.dueSoon].slice(0, 6).map((r) => (
                                <li key={r.id}>
                                    <button
                                        onClick={() => navigate(`/customers/${r.customer.id}`)}
                                        className="w-full flex items-center gap-4 px-5 md:px-6 py-3.5 text-left hover:bg-[#8b5cf6]/5 transition-colors"
                                    >
                                        <div className="p-2 rounded-xl bg-slate-900 border border-slate-700 text-[#8b5cf6] shrink-0">
                                            <Monitor size={16} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-white truncate">{r.customer.name}</p>
                                            <p className="text-xs text-slate-500 truncate">
                                                {r.product.name}
                                                {r.subscriptionPlan ? ` · ${r.subscriptionPlan.name}` : ""}
                                            </p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="text-sm font-semibold text-slate-200">{formatMoney(r.renewalAmount ?? r.amount)}</p>
                                            <div className="flex items-center justify-end gap-2 mt-0.5">
                                                <span className="text-xs text-slate-500 hidden sm:inline">{formatDate(r.renewalDate)}</span>
                                                <RenewalChip date={r.renewalDate} status={r.status} />
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                {/* Subscription status + mix */}
                <Card>
                    <CardHeader title="Subscriptions" subtitle="Software by status" icon={Package} />
                    <StatusBars
                        total={stats.softwareCount}
                        rows={[
                            { label: "Active", value: stats.subStatus.ACTIVE ?? 0, color: "bg-emerald-400", to: "/customer-products?type=SOFTWARE&status=ACTIVE" },
                            { label: "Pending", value: stats.subStatus.PENDING ?? 0, color: "bg-slate-400", to: "/customer-products?type=SOFTWARE&status=PENDING" },
                            { label: "Suspended", value: stats.subStatus.SUSPENDED ?? 0, color: "bg-orange-400", to: "/customer-products?type=SOFTWARE&status=SUSPENDED" },
                            { label: "Cancelled", value: stats.subStatus.CANCELLED ?? 0, color: "bg-red-400", to: "/customer-products?type=SOFTWARE&status=CANCELLED" },
                        ]}
                    />

                    <div className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t border-slate-800">
                        <MixTile icon={Cpu} label="Hardware" value={stats.hardwareCount} to="/customer-products?type=HARDWARE" />
                        <MixTile icon={Monitor} label="Software" value={stats.softwareCount} to="/customer-products?type=SOFTWARE" />
                    </div>
                </Card>

                {/* Pending deliveries */}
                <Card className="xl:col-span-2" padded={false}>
                    <div className="p-5 md:p-6 pb-0 md:pb-0">
                        <CardHeader
                            title="Pending deliveries"
                            subtitle="Hardware not yet delivered"
                            icon={Truck}
                            action={<ViewAll to="/customer-products?type=HARDWARE&deliveryStatus=PENDING" />}
                        />
                    </div>
                    {stats.pendingDeliveries.length === 0 ? (
                        <EmptyState title="All delivered" text="No hardware is waiting for delivery." />
                    ) : (
                        <ul className="divide-y divide-slate-800/60">
                            {stats.pendingDeliveries.slice(0, 5).map((r) => (
                                <li key={r.id}>
                                    <button
                                        onClick={() => navigate(`/customers/${r.customer.id}`)}
                                        className="w-full flex items-center gap-4 px-5 md:px-6 py-3.5 text-left hover:bg-[#8b5cf6]/5 transition-colors"
                                    >
                                        <div className="p-2 rounded-xl bg-slate-900 border border-slate-700 text-sky-400 shrink-0">
                                            <Cpu size={16} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-white truncate">{r.product.name}</p>
                                            <p className="text-xs text-slate-500 truncate">{r.customer.name}</p>
                                        </div>
                                        <div className="text-right shrink-0 space-y-1">
                                            <StatusBadge value={r.deliveryStatus} />
                                            <p className="text-xs text-slate-500">{r.deliveryDate ? formatDate(r.deliveryDate) : "No date"}</p>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>

                {/* Recent customers */}
                <Card padded={false}>
                    <div className="p-5 md:p-6 pb-0 md:pb-0">
                        <CardHeader title="Recent customers" icon={Users} action={<ViewAll to="/customers" />} />
                    </div>
                    {stats.recentCustomers.length === 0 ? (
                        <EmptyState
                            title="No customers yet"
                            action={
                                <Button size="sm" icon={Plus} onClick={() => setCustomerFormOpen(true)}>
                                    Add customer
                                </Button>
                            }
                        />
                    ) : (
                        <ul className="divide-y divide-slate-800/60">
                            {stats.recentCustomers.map((c) => (
                                <li key={c.id}>
                                    <Link to={`/customers/${c.id}`} className="flex items-center gap-3 px-5 md:px-6 py-3 hover:bg-[#8b5cf6]/5 transition-colors">
                                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#8b5cf6]/30 to-[#6d28d9]/30 border border-[#8b5cf6]/20 flex items-center justify-center text-xs font-bold text-[#c4b5fd] shrink-0">
                                            {c.name.slice(0, 2).toUpperCase()}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-white truncate">{c.name}</p>
                                            <p className="text-xs text-slate-500">
                                                #{c.customerId}
                                                {isAdmin && c.seller ? ` · ${c.seller.name}` : ""}
                                            </p>
                                        </div>
                                        <span className="text-xs text-slate-500 shrink-0">{formatDate(c.createdAt)}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>

            <CustomerFormModal
                open={customerFormOpen}
                customer={null}
                onClose={() => setCustomerFormOpen(false)}
                onSaved={(c) => {
                    setCustomerFormOpen(false);
                    navigate(`/customers/${c.id}`);
                }}
            />

            <CustomerProductFormModal
                open={assignOpen}
                item={null}
                onClose={() => setAssignOpen(false)}
                onSaved={() => {
                    setAssignOpen(false);
                    productsReq.reload();
                }}
            />
        </>
    );
}

/* ---------- small dashboard widgets ---------- */

function StatCard({
    index,
    to,
    icon: Icon,
    label,
    value,
    sub,
    alert,
}: {
    index: number;
    to: string;
    icon: React.ElementType;
    label: string;
    value: string;
    sub: string;
    alert?: boolean;
}) {
    return (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
            <Link
                to={to}
                className="group block h-full p-5 rounded-3xl bg-slate-900/40 backdrop-blur-2xl border border-slate-800 hover:border-[#8b5cf6]/40 transition-all relative overflow-hidden"
            >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#8b5cf6]/10 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-start justify-between relative">
                    <div className={`p-2.5 rounded-xl border ${alert ? "bg-amber-500/10 border-amber-500/20 text-amber-400" : "bg-slate-900 border-slate-700 text-[#8b5cf6]"}`}>
                        <Icon size={18} />
                    </div>
                    <ArrowRight size={16} className="text-slate-600 group-hover:text-[#a78bfa] group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-3xl font-bold text-white mt-4 tracking-tight relative">{value}</p>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-1 relative">{label}</p>
                <p className={`text-xs mt-2 truncate relative ${alert ? "text-amber-400/80" : "text-slate-500"}`}>{sub}</p>
            </Link>
        </motion.div>
    );
}

function ViewAll({ to }: { to: string }) {
    return (
        <Link to={to} className="inline-flex items-center gap-1 text-xs font-semibold text-[#a78bfa] hover:text-white transition-colors whitespace-nowrap">
            View all
            <ArrowRight size={14} />
        </Link>
    );
}

function SalesChart({ months }: { months: { label: string; hardware: number; software: number }[] }) {
    const max = Math.max(1, ...months.map((m) => m.hardware + m.software));
    const [hover, setHover] = useState<number | null>(null);

    return (
        <div>
            <div className="flex items-end gap-3 sm:gap-5 h-52 pt-6">
                {months.map((m, i) => {
                    const total = m.hardware + m.software;
                    const h = (total / max) * 100;
                    const swShare = total ? (m.software / total) * 100 : 0;
                    return (
                        <div
                            key={m.label + i}
                            className="flex-1 h-full flex flex-col items-center justify-end gap-2 relative"
                            onMouseEnter={() => setHover(i)}
                            onMouseLeave={() => setHover(null)}
                        >
                            {hover === i && total > 0 && (
                                <div className="absolute bottom-full mb-1 z-10 px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs whitespace-nowrap shadow-xl">
                                    <p className="font-bold text-white">{formatMoney(total)}</p>
                                    <p className="text-sky-400">HW {formatMoney(m.hardware)}</p>
                                    <p className="text-[#a78bfa]">SW {formatMoney(m.software)}</p>
                                </div>
                            )}
                            <span className="text-[10px] text-slate-500 font-semibold">{total ? formatCompact(total) : ""}</span>
                            <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: `${Math.max(h, total ? 3 : 1)}%` }}
                                transition={{ duration: 0.6, delay: i * 0.05, ease: "easeOut" }}
                                className={`w-full max-w-[48px] rounded-xl overflow-hidden flex flex-col-reverse ${total ? "" : "bg-slate-800/60"} ${
                                    hover === i ? "ring-2 ring-[#8b5cf6]/40" : ""
                                }`}
                            >
                                {total > 0 && (
                                    <>
                                        <div className="bg-sky-500/70" style={{ height: `${100 - swShare}%` }} />
                                        <div className="bg-gradient-to-t from-[#7c3aed] to-[#a78bfa]" style={{ height: `${swShare}%` }} />
                                    </>
                                )}
                            </motion.div>
                            <span className="text-xs text-slate-500">{m.label}</span>
                        </div>
                    );
                })}
            </div>
            <div className="flex gap-4 mt-4 text-xs text-slate-400">
                <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-[#8b5cf6]" />
                    Software
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-sky-500/70" />
                    Hardware
                </span>
            </div>
        </div>
    );
}

function Donut({ segments }: { segments: { label: string; value: number; color: string; to: string }[] }) {
    const total = segments.reduce((s, x) => s + x.value, 0);
    const r = 52;
    const c = 2 * Math.PI * r;
    let offset = 0;

    return (
        <div className="flex flex-col items-center">
            <div className="relative w-44 h-44">
                <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
                    <circle cx="70" cy="70" r={r} fill="none" stroke="#1e293b" strokeWidth="16" />
                    {total > 0 &&
                        segments.map((s) => {
                            const len = (s.value / total) * c;
                            const el = (
                                <motion.circle
                                    key={s.label}
                                    cx="70"
                                    cy="70"
                                    r={r}
                                    fill="none"
                                    stroke={s.color}
                                    strokeWidth="16"
                                    strokeDasharray={`${len} ${c - len}`}
                                    strokeDashoffset={-offset}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.6 }}
                                />
                            );
                            offset += len;
                            return el;
                        })}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-3xl font-bold text-white">{total}</p>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">records</p>
                </div>
            </div>
            <div className="w-full mt-6 space-y-1">
                {segments.map((s) => (
                    <Link key={s.label} to={s.to} className="flex items-center justify-between px-3 py-2 rounded-xl hover:bg-slate-800/40 transition-colors">
                        <span className="flex items-center gap-2 text-sm text-slate-300">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                            {s.label}
                        </span>
                        <span className="text-sm font-semibold text-white">
                            {s.value}
                            <span className="text-xs text-slate-500 font-normal ml-1.5">{total ? Math.round((s.value / total) * 100) : 0}%</span>
                        </span>
                    </Link>
                ))}
            </div>
        </div>
    );
}

function StatusBars({ total, rows }: { total: number; rows: { label: string; value: number; color: string; to: string }[] }) {
    return (
        <div className="space-y-4">
            {rows.map((r, i) => (
                <Link key={r.label} to={r.to} className="block group">
                    <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-slate-300 group-hover:text-white transition-colors">{r.label}</span>
                        <span className="font-semibold text-white">{r.value}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${total ? (r.value / total) * 100 : 0}%` }}
                            transition={{ duration: 0.6, delay: i * 0.05 }}
                            className={`h-full rounded-full ${r.color}`}
                        />
                    </div>
                </Link>
            ))}
        </div>
    );
}

function MixTile({ icon: Icon, label, value, to }: { icon: React.ElementType; label: string; value: number; to: string }) {
    return (
        <Link to={to} className="p-3 rounded-2xl bg-slate-800/30 border border-slate-800 hover:border-[#8b5cf6]/30 transition-colors">
            <Icon size={16} className="text-[#8b5cf6]" />
            <p className="text-xl font-bold text-white mt-2">{value}</p>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</p>
        </Link>
    );
}